// src/components/auth/Signin.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../layout/Header';
import Navigation from '../layout/Navigation';

/* ─── Shared design system (mirrors Signup) ─── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

  :root {
    --ink:    #1c1d2e;
    --slate:  #41425A;
    --mid:    #5a5b75;
    --mist:   #DBDCED;
    --accent: #6c63ff;
    --green:  #22c55e;
    --red:    #ef4444;
  }

  .sgn-root * { font-family: 'DM Sans', sans-serif; }

  /* orbs */
  @keyframes orb-drift {
    0%,100% { transform: translate(0,0) scale(1); }
    33%      { transform: translate(40px,-30px) scale(1.08); }
    66%      { transform: translate(-20px,20px) scale(0.94); }
  }
  .orb { position:absolute; border-radius:50%; filter:blur(70px); opacity:.35; animation: orb-drift 14s ease-in-out infinite; pointer-events:none; }
  .orb-1 { width:380px;height:380px;background:radial-gradient(circle,#6c63ff,transparent 70%); top:-100px;left:-80px; animation-delay:0s; }
  .orb-2 { width:280px;height:280px;background:radial-gradient(circle,#a78bfa,transparent 70%); bottom:-60px;right:-60px; animation-delay:-5s; }
  .orb-3 { width:200px;height:200px;background:radial-gradient(circle,#38bdf8,transparent 70%); top:55%;left:65%; animation-delay:-9s; }
  .orb-dk-1 { background:radial-gradient(circle,#4338ca,transparent 70%); opacity:.25; }
  .orb-dk-2 { background:radial-gradient(circle,#7c3aed,transparent 70%); opacity:.2; }
  .orb-dk-3 { background:radial-gradient(circle,#0ea5e9,transparent 70%); opacity:.15; }

  /* card entrance */
  @keyframes card-in {
    from { opacity:0; transform: translateY(28px) scale(.97); }
    to   { opacity:1; transform: translateY(0) scale(1); }
  }
  .card-animate { animation: card-in .55s cubic-bezier(.22,.68,0,1.2) both; }

  /* floating label */
  .fl-wrap { position:relative; }
  .fl-wrap input {
    padding: 22px 14px 8px;
    font-size: 15px;
    line-height: 1.4;
    width:100%;
    border-radius:14px;
    border:2px solid transparent;
    outline:none;
    transition: border-color .2s, box-shadow .2s, background .2s;
  }
  .fl-wrap label {
    position:absolute; left:15px; top:50%;
    transform:translateY(-50%);
    font-size:14px; pointer-events:none;
    transition: top .2s, font-size .2s, color .2s;
    font-family:'DM Sans',sans-serif;
  }
  .fl-wrap input:focus + label,
  .fl-wrap input:not(:placeholder-shown) + label {
    top:10px; font-size:11px; font-weight:600; letter-spacing:.05em; text-transform:uppercase;
  }
  .fl-wrap input:focus { border-color: var(--accent); box-shadow: 0 0 0 4px rgba(108,99,255,.15); }

  .fl-light input { background:#f5f5fb; border-color:#e0e0ee; color:var(--ink); }
  .fl-light label { color:#9090b0; }
  .fl-light input:focus + label, .fl-light input:not(:placeholder-shown) + label { color:var(--accent); }
  .fl-light input::placeholder { color:transparent; }

  .fl-dark input { background:rgba(255,255,255,.06); border-color:rgba(255,255,255,.1); color:#e8e8f4; }
  .fl-dark label { color:#8888aa; }
  .fl-dark input:focus + label, .fl-dark input:not(:placeholder-shown) + label { color:#a78bfa; }
  .fl-dark input::placeholder { color:transparent; }

  /* button shine */
  @keyframes shine { from{left:-120%} to{left:120%} }
  .btn-primary { position:relative; overflow:hidden; }
  .btn-primary::after {
    content:''; position:absolute; top:0; left:-120%;
    width:60%; height:100%;
    background:linear-gradient(90deg,transparent,rgba(255,255,255,.25),transparent);
    transform:skewX(-20deg);
  }
  .btn-primary:hover::after { animation: shine .55s ease; }

  /* shake */
  @keyframes shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-6px)} 40%{transform:translateX(6px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }
  .shake { animation: shake .4s ease; }

  /* icon float */
  @keyframes icon-float { 0%,100%{transform:translateY(0) rotate(-3deg)} 50%{transform:translateY(-8px) rotate(3deg)} }
  .icon-float { animation: icon-float 3.5s ease-in-out infinite; }

  /* success pulse */
  @keyframes success-pulse { 0%,100%{box-shadow:0 0 0 0 rgba(34,197,94,.4)} 50%{box-shadow:0 0 0 10px rgba(34,197,94,0)} }
  .success-pulse { animation: success-pulse 1.5s ease infinite; }

  /* loading spinner */
  @keyframes spin { to{transform:rotate(360deg)} }
  .spinner { display:inline-block; width:18px; height:18px; border:2px solid rgba(255,255,255,.3); border-top-color:#fff; border-radius:50%; animation:spin .7s linear infinite; }
`;

const Signin = () => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shakeError, setShakeError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const errorRef = useRef(null);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(savedTheme === 'dark' || (!savedTheme && prefersDark));
  }, []);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(fd => ({ ...fd, [name]: value }));
    setErrorMessage('');
    setSuccessMessage('');
  };

  const triggerError = (msg) => {
    setErrorMessage(msg);
    setShakeError(true);
    setTimeout(() => setShakeError(false), 420);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { username, password } = formData;
    if (!username.trim() || !password) { triggerError('Please fill in all fields.'); return; }
    setIsLoading(true);

    try {
      const SIGNIN_URL = process.env.REACT_APP_SIGNIN_URL;
      const response = await axios.post(SIGNIN_URL, { username, password });

      if (response.data.success) {
        localStorage.setItem('token', response.data.token);
        try {
          const profileResponse = await axios.get('http://localhost:5000/api/user/profile', {
            headers: { Authorization: response.data.token }
          });
          if (profileResponse.data.success) {
            const mergedUser = { ...profileResponse.data.user, lastLogin: response.data.user.lastLogin };
            localStorage.setItem('user', JSON.stringify(mergedUser));
          } else {
            localStorage.setItem('user', JSON.stringify(response.data.user));
          }
        } catch (profileError) {
          console.error('Error fetching profile data:', profileError);
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
        setSuccessMessage('Sign In Successful! Redirecting…');
        setErrorMessage('');
        setTimeout(() => navigate('/mainscreen'), 1500);
      } else {
        triggerError(response.data.message);
      }
    } catch (err) {
      console.error('Sign In error:', err);
      if (err.response?.status === 401) triggerError('Invalid username or password.');
      else triggerError('Something went wrong, please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{STYLES}</style>

      <div className={`sgn-root flex flex-col min-h-screen transition-colors duration-500 overflow-x-hidden ${
        isDarkMode
          ? 'bg-gradient-to-br from-[#0a0a1a] via-[#0f0f24] to-[#14102a]'
          : 'bg-gradient-to-br from-[#f0f0fb] via-[#f8f8ff] to-[#eeeef8]'
      }`}>

        {/* Background orbs */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
          <div className={`orb orb-1 ${isDarkMode ? 'orb-dk-1' : ''}`} />
          <div className={`orb orb-2 ${isDarkMode ? 'orb-dk-2' : ''}`} />
          <div className={`orb orb-3 ${isDarkMode ? 'orb-dk-3' : ''}`} />
        </div>

        <div className="relative" style={{ zIndex: 10 }}>
          <Header isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
          <Navigation isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
        </div>

        <div className="flex-grow flex justify-center items-center py-10 px-4 pt-[150px] pb-12" style={{ zIndex: 5, position: 'relative' }}>
          <div className="card-animate w-full max-w-md">

            {/* Glassmorphic card */}
            <div
              className={`relative rounded-3xl overflow-hidden border transition-colors duration-500 ${
                isDarkMode
                  ? 'bg-[#12122a]/80 border-white/10 backdrop-blur-2xl'
                  : 'bg-white/75 border-white/60 backdrop-blur-xl'
              }`}
              style={{ boxShadow: isDarkMode
                ? '0 32px 80px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.06)'
                : '0 24px 70px rgba(108,99,255,.12), inset 0 1px 0 rgba(255,255,255,.9)' }}
            >
              {/* top accent strip */}
              <div className="h-1 w-full bg-gradient-to-r from-violet-500 via-indigo-400 to-blue-400" />

              <div className="p-8 md:p-10">

                {/* Icon + heading */}
                <div className="text-center mb-9">
                  <div className="inline-flex items-center justify-center mb-4">
                    <span className="text-5xl icon-float">🔐</span>
                  </div>
                  <h2
                    className={`text-3xl md:text-4xl font-extrabold tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                    style={{ fontFamily: 'Sora, sans-serif' }}
                  >
                    Welcome Back
                  </h2>
                  <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Sign in to continue to your workspace
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" noValidate>

                  {/* Username */}
                  <div className={`fl-wrap ${isDarkMode ? 'fl-dark' : 'fl-light'} ${formData.username ? 'has-val' : ''}`}>
                    <input
                      type="text" name="username"
                      value={formData.username} onChange={handleChange}
                      required placeholder=" "
                      autoComplete="username"
                    />
                    <label htmlFor="username"><span className="mr-1">👤</span>Username</label>
                  </div>

                  {/* Password with show/hide */}
                  <div className="relative">
                    <div className={`fl-wrap ${isDarkMode ? 'fl-dark' : 'fl-light'} ${formData.password ? 'has-val' : ''}`}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password} onChange={handleChange}
                        required placeholder=" "
                        autoComplete="current-password"
                        style={{ paddingRight: '48px' }}
                      />
                      <label htmlFor="password"><span className="mr-1">🔒</span>Password</label>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs px-2 py-1 rounded-lg font-medium transition-colors ${
                        isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
                      }`}
                      tabIndex={-1}
                    >
                      {showPassword ? '🙈 Hide' : '👁 Show'}
                    </button>
                  </div>

                  {/* Forgot password */}
                  <div className="flex justify-end -mt-1">
                    <a href="/forgot-password" className={`text-xs font-medium hover:underline ${isDarkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'}`}>
                      Forgot password?
                    </a>
                  </div>

                  {/* Error */}
                  {errorMessage && (
                    <div
                      ref={errorRef}
                      className={`${shakeError ? 'shake' : ''} flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium bg-red-50 border border-red-200 text-red-700`}
                    >
                      <span>⚠️</span> {errorMessage}
                    </div>
                  )}

                  {/* Success */}
                  {successMessage && (
                    <div className="success-pulse flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium bg-green-50 border border-green-200 text-green-700">
                      <span>✅</span> {successMessage}
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary w-full py-4 px-6 bg-gradient-to-r from-[#6c63ff] to-[#8b5cf6] hover:from-[#5b52ee] hover:to-[#7c3aed] text-white font-semibold text-base rounded-2xl transition-all duration-200 hover:scale-[1.02] active:scale-95 shadow-lg hover:shadow-violet-500/30 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-3"
                    style={{ fontFamily: 'Sora, sans-serif' }}
                  >
                    {isLoading ? (
                      <><span className="spinner" /> Signing in…</>
                    ) : (
                      <>Sign In →</>
                    )}
                  </button>

                  {/* divider */}
                  <div className="flex items-center gap-3 py-1">
                    <div className={`flex-1 h-px ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                    <span className={`text-xs ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>or</span>
                    <div className={`flex-1 h-px ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  </div>

                  {/* Sign up link */}
                  <p className={`text-center text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    New here?{' '}
                    <a href="/signup" className={`font-semibold hover:underline ${isDarkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'}`}>
                      Create an account →
                    </a>
                  </p>
                </form>
              </div>
            </div>

            <p className={`text-center text-xs mt-5 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              🔒 Your data is encrypted and secure
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Signin;